/* eslint-disable max-len */

import { useTranslation } from 'react-i18next';
import {
  capitalizeFirstLetterEveryword
} from '../../../shared/stringFunctions';
import { TXT_ADD_ROLE, TXT_ROLE_MANAGEMENT } from '../../../translations/translationConstants';
import {
  TXT_SEARCH_BY_NAME
} from '../../../translations/translationConstants';
import { ApolloError, useLazyQuery, useMutation } from '@apollo/client';
import {
  addApplicationRoleInput,
  deleteApplicationRoleInput,
  getApplicationRolesInput,
  getApplicationRolesOutput,
  GET_APPLICATION_ROLES,
  updateApplicationRoleInput
} from '../../../api';
import { useEffect } from 'react';
import { useState } from 'react';
import { ManageRolesTable } from './ManageRolesTable';
import {
  DeleteConfirmation,
  globalPageSizeOptions,
  PaginationBar
} from '../../../components';
import { applicationRole } from '../../../entityModel';
import { useApolloErrorHandler, useAuthServices } from '../../../hooks';
import {
  ADD_APPLICATION_ROLE,
  DELETE_APPLICATION_ROLE,
  UPDATE_APPLICATION_ROLE
} from '../../../api/graphql/applicationRole/applicationRoleMutations';
import { ManageRoleEditor } from './ManagerRoleEditor';

export const ManageRoles = () => {
  const { t } = useTranslation();
  const { isSignedIn } = useAuthServices();

  const [txtSearch, setTxtSearch] = useState('');

  const onTxtSearchChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTxtSearch(event.currentTarget.value);
  };

  const [selectedRole, setSelectedRole] = useState<applicationRole | undefined>(undefined);
  const [getRoles, { loading, data, error, refetch }] = useLazyQuery<getApplicationRolesOutput>(GET_APPLICATION_ROLES);

  useEffect(() => {
    getRoles();
  }, []);

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(globalPageSizeOptions[0]);

  const [openEditor, setOpenEditor] = useState(false);
  const [savingError, setSavingError] = useState<string | undefined>(undefined);

  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);

  const lblSearch = capitalizeFirstLetterEveryword(t(TXT_SEARCH_BY_NAME));

  const fetchRoles = () => {
    const variables: getApplicationRolesInput = {
      name: txtSearch,
      take: pageSize,
      skip: (pageNumber - 1) * pageSize
    };
    getRoles({
      variables
    });
  };

  const fetch = (resetPage?: boolean) => {
    if (!!resetPage && pageNumber !== 1) {
      setPageNumber(1);
    } else {
      fetchRoles();
    }
  };

  useEffect(() => {
    useApolloErrorHandler(isSignedIn, error);
  }, [error]);

  useEffect(() => {
    fetch();
  }, [pageNumber]);

  useEffect(() => {
    fetch(true);
  }, [pageSize, txtSearch]);

  const openEditRoleEditor = (role: applicationRole) => {
    setSelectedRole(role);
    setOpenEditor(true);
  };

  const openDeleteRoleConfirmation = (role: applicationRole) => {
    setSelectedRole(role);
    setOpenDeleteConfirmation(true);
  };

  const openAddRoleEditor = () => {
    setSelectedRole(undefined);
    setOpenEditor(true);
  };

  const closeEditor = () => {
    setOpenEditor(false);
  };

  const afterAddingOrUpdatingRole = () => {
    setOpenEditor(false);
    if (refetch) {
      refetch();
    }
  };

  const handleAddingOrUpdatingRoleError = (error: ApolloError) => {
    if (error.graphQLErrors.length > 0 &&
      !!error.graphQLErrors[0].extensions) {
      setSavingError(error.graphQLErrors[0].extensions['message']);
    } else {
      setSavingError(error.message);
    }
  };

  const [addRole, addRoleOperation] = useMutation<applicationRole>(ADD_APPLICATION_ROLE, {
    onError: (e) => handleAddingOrUpdatingRoleError(e),
    onCompleted: (result) => afterAddingOrUpdatingRole()
  });

  const [updateRole, updateRoleOperation] = useMutation<applicationRole>(UPDATE_APPLICATION_ROLE, {
    onError: (e) => handleAddingOrUpdatingRoleError(e),
    onCompleted: (result) => afterAddingOrUpdatingRole()
  });

  const onEditorSave = (payload: addApplicationRoleInput | updateApplicationRoleInput) => {
    if (!selectedRole) {
      addRole({
        variables: {
          input: payload
        }
      });
    } else {
      updateRole({
        variables: {
          input: payload
        }
      });
    }
  };

  const onDeleteCancel = () => setOpenDeleteConfirmation(false);

  const [deleteRole, deleteRoleOperation] = useMutation<boolean>(DELETE_APPLICATION_ROLE, {
    onError: (e) => { },
    onCompleted: (result) => {
      if (data && refetch) {
        if (data.roles.items.length == 1) {
          const targetPage = pageNumber - 1 <= 0 ? 1 : pageNumber - 1;
          if (pageNumber !== targetPage) {
            setPageNumber(targetPage);
          } else {
            refetch();
          }
        } else {
          refetch();
        }
      }
      onDeleteCancel();
    }
  });

  const onDelete = () => {
    const input: deleteApplicationRoleInput = {
      id: selectedRole!.id!
    };
    deleteRole({
      variables: {
        input
      }
    });
  };

  const onPageSizeChanged = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPageSize(+event.target.value);
  };

  return (
    <div className="content-wrapper">
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1>{t(capitalizeFirstLetterEveryword(TXT_ROLE_MANAGEMENT))}</h1>
            </div>
          </div>
        </div>
      </section>
      <section className="content">
        <div className="container-fluid">
          {
            openEditor &&
            <ManageRoleEditor
              role={selectedRole}
              onClose={closeEditor}
              onSave={onEditorSave}
              isSaving={
                !selectedRole ? addRoleOperation.loading :
                  updateRoleOperation.loading
              }
              savingError={savingError}
            />
          }
          {
            openDeleteConfirmation &&
            <DeleteConfirmation
              loading={deleteRoleOperation.loading}
              onYes={onDelete}
              onNo={onDeleteCancel}
              objectName={selectedRole?.name || ''}
            />
          }
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <div className='row'>
                    <div className='col-6'>
                      <div className="form-group">
                        <label>{lblSearch}</label>
                        <input type='text' className='form-control' value={txtSearch} onChange={onTxtSearchChanged} />
                      </div>
                    </div>
                  </div>
                  <div className='row'>
                    <div className='col-6'>
                      <button
                        className='btn btn-primary'
                        data-target="#modal-role-editor"
                        data-toggle="modal"
                        disabled={loading}
                        onClick={openAddRoleEditor}>
                        {t(TXT_ADD_ROLE).toUpperCase()}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="card-body">
                  <ManageRolesTable
                    loading={loading}
                    data={data}
                    error={error}
                    onEdit={openEditRoleEditor}
                    onDelete={openDeleteRoleConfirmation}
                  />
                </div>
                <PaginationBar
                  currentPage={pageNumber}
                  totalCount={data?.roles.totalCount}
                  pageSize={pageSize}
                  onPageChanged={(page) => setPageNumber(page)}
                  onChangeRowsPerPage={onPageSizeChanged}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
